import { Link } from "react-router-dom";
import { Leaf, ArrowRight, Lock, Sun } from "lucide-react";
import ProviderLogo from "./ProviderLogo";
import { statePath } from "@/lib/stateRegistry";
import CostBasisLine from "@/components/CostBasisLine";
import DocumentLinks from "@/components/DocumentLinks";
import usePlanCosts, { formatDollars, formatLongDate, tierFor } from "@/lib/planCosts";
import { formatSolarBuyback } from "@/lib/solarBuyback";
import { EARLY_TERMINATION_FAQ_PATH, ETF_EXCEPTIONS_INLINE_LINK_TEXT } from "@/lib/etfExceptionsTexts";
import { resolveEtfBuyoutOffer } from "@/lib/etfBuyoutOffer";
import { SHOW_ETF_BUYOUT_PROMO } from "@/lib/displayFlags";

// `usageTiers` (Texas EFL 3-tier display) comes from the state registry, never from inspecting the plan.
export default function PlanCard({ plan, provider, usageLevel = 1000, stateSlug, usageTiers = false }) {
  const isTexas = usageTiers;
  const rates = [
    { kwh: 500, rate: plan.rate_500 },
    { kwh: 1000, rate: plan.rate_1000 },
    { kwh: 2000, rate: plan.rate_2000 },
  ];
  const selected = rates.find((r) => r.kwh === usageLevel) || rates[1];
  // Estimated monthly cost comes from the server (GET /api/plans/{id}/costs). Nothing is computed here.
  const costs = usePlanCosts(plan.id);
  const costKwh = isTexas ? selected.kwh : 1000;
  const tier = tierFor(costs, costKwh);
  const verifiedOn = formatLongDate(plan.verified_at);
  const solarBuyback = formatSolarBuyback(plan);
  const buyoutOffer = SHOW_ETF_BUYOUT_PROMO ? resolveEtfBuyoutOffer(plan) : null;

  return (
    <article
      className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
      data-testid={`plan-card-${plan.id}`}
    >
      <div className="grid grid-cols-12 gap-6 items-center">
        {/* Provider + name */}
        <div className="col-span-12 md:col-span-3 flex items-center gap-3">
          <div className="rounded-xl overflow-hidden">
            <ProviderLogo provider={provider} size={48} />
          </div>
          <div>
            <p className="text-xs text-slate-500">{provider?.name || "Provider"}</p>
            <h3 className="font-display text-lg leading-tight font-bold text-[#0A2540]">{plan.name}</h3>
            {solarBuyback && (
              <span
                className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#FFF7E6] text-[#B45309]"
                data-testid={`plan-solar-buyback-${plan.id}`}
              >
                <Sun className="w-3 h-3" /> {solarBuyback}
              </span>
            )}
          </div>
        </div>

        {/* Rate display — 3-tier for Texas (PUCT), single rate elsewhere */}
        {isTexas ? (
          <div className="col-span-12 md:col-span-4 md:border-l border-slate-100 md:pl-6" data-testid={`plan-rates-${plan.id}`}>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Price Per kWh At Usage</p>
            <div className="grid grid-cols-3 gap-2">
              {rates.map((r) => {
                const isSel = r.kwh === usageLevel;
                return (
                  <div
                    key={r.kwh}
                    className={`rounded-lg px-2 py-2 text-center transition-colors ${
                      isSel
                        ? "bg-[#DCFCE7] border border-[#16A34A]"
                        : "bg-slate-50 border border-slate-100"
                    }`}
                    data-testid={`rate-${plan.id}-${r.kwh}`}
                  >
                    <p className={`text-[10px] font-mono ${isSel ? "text-[#15803D]" : "text-slate-500"}`}>{r.kwh} kWh</p>
                    <p className={`font-display text-lg font-extrabold ${isSel ? "text-[#15803D]" : "text-[#0A2540]"}`}>
                      {r.rate.toFixed(1)}<span className="text-xs font-normal">¢</span>
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="col-span-12 md:col-span-4 md:border-l border-slate-100 md:pl-6" data-testid={`plan-rate-single-${plan.id}`}>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Rate</p>
            <p className="font-display text-4xl font-extrabold tracking-tight text-[#0A2540]">
              {plan.rate_1000.toFixed(1)}
              <span className="text-lg font-normal text-slate-400">¢ / kWh</span>
            </p>
          </div>
        )}

        {/* Term + green */}
        <div className="col-span-6 md:col-span-2 md:border-l border-slate-100 md:pl-6">
          <p className="text-xs text-slate-500">Term</p>
          <p className="font-display text-xl font-bold flex items-center gap-1 text-[#0A2540]">
            <Lock className="w-4 h-4" /> {plan.term_months}<span className="text-sm font-normal text-slate-400">mo</span>
          </p>
          <span
            className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
              plan.green_energy_pct >= 100
                ? "bg-emerald-50 text-emerald-700"
                : plan.green_energy_pct > 0
                ? "bg-amber-50 text-amber-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            <Leaf className="w-3 h-3" /> {plan.green_energy_pct}%
          </span>
          {/* Site audit (2026-09-28): category-leading comparison sites (e.g. ComparePower) surface the
              Early Termination Fee on the plan CARD, not only the detail page, since it's a real decision
              factor when comparing similarly-priced plans. The field already exists on every plan
              (backend/catalog/models.py) and was previously only shown in PlanDetail's "Fine Print" --
              this closes that card-level information-density gap with zero new data. A $0 ETF is framed
              as a positive ("No cancellation fee"), not just a "$0.00" line. */}
          <p className="text-[10px] text-slate-500 mt-1.5" data-testid={`plan-etf-${plan.id}`}>
            {plan.early_termination_fee > 0
              ? (
                <>
                  {formatDollars(plan.early_termination_fee)} early termination fee —{" "}
                  <Link to={EARLY_TERMINATION_FAQ_PATH} className="underline" data-testid={`plan-etf-exceptions-link-${plan.id}`}>
                    {ETF_EXCEPTIONS_INLINE_LINK_TEXT}
                  </Link>
                </>
              )
              : "No cancellation fee"}
          </p>
          {/* Section 4 (provisional, feature-flagged pending the CFO compensation-parity question):
              same visual lane/weight as any other perk -- own line, no featured styling, no rank influence.
              Attribution keeps the supplier as the grammatical subject of the offer; RC is a neutral
              reporter of it, never "our pick." Links out to the supplier's own offer page rather than
              restating terms as RC-authoritative. */}
          {buyoutOffer && (
            <p className="text-[10px] text-slate-500 mt-1" data-testid={`plan-etf-buyout-${plan.id}`}>
              <strong>{provider?.name ? `${provider.name}'s offer` : "This provider's offer"}</strong>: up to{" "}
              {formatDollars(buyoutOffer.amountCents / 100)} toward your current provider's early termination
              fee when you switch. Offer expires {formatLongDate(buyoutOffer.promoExpiresAt)}. Verified{" "}
              {formatLongDate(buyoutOffer.verifiedAt)}.{" "}
              <a
                href={buyoutOffer.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                data-testid={`plan-etf-buyout-link-${plan.id}`}
              >
                See {provider?.name || "the provider"}'s current offer terms →
              </a>
            </p>
          )}
        </div>

        {/* Estimated bill + CTA */}
        <div className="col-span-12 md:col-span-3 md:border-l border-slate-100 md:pl-6 flex flex-col gap-2 md:items-end">
          <div className="md:text-right">
            <p className="text-xs text-slate-500">Estimated Monthly Cost</p>
            <p className="font-display text-2xl font-extrabold text-[#0A2540]" data-testid={`plan-cost-${plan.id}`} aria-busy={costs.status === "loading"}>
              {costs.status === "loading" ? "…" : tier ? formatDollars(tier.estimated_monthly_cost) : "Unavailable"}
            </p>
            <p className="text-[10px] text-slate-400">At {costKwh} kWh</p>
            {verifiedOn && (
              <p className="text-[10px] text-slate-500" data-testid={`plan-verified-${plan.id}`}>Rates Verified As Of {verifiedOn}</p>
            )}
            <CostBasisLine plan={plan} variant="short" testId={`cost-basis-${plan.id}`} className="text-[11px] text-slate-600 mt-1 max-w-[16rem] md:ml-auto" />
          </div>
          <Link
            to={statePath(stateSlug, `plans/${plan.id}`)}
            className="btn-green w-full md:w-auto !py-2.5"
            aria-label={`View plan details for ${plan.name}${provider?.name ? ` from ${provider.name}` : ""}`}
            data-testid={`view-plan-${plan.id}`}
          >
            View Plan <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <DocumentLinks plan={plan} planName={plan.name} variant="row" className="md:text-right" testId={`plan-docs-${plan.id}`} />
        </div>
      </div>
    </article>
  );
}
